// DESCRIPTION: Calendar toolbar — prev/next navigation, today button, and view switcher buttons.

import React from 'react';
import { getTimezoneAbbr, getLocalTimezoneAbbr } from './utils';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

const MONTH_NAMES = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

/**
 * Format the heading text for the current view and anchor date.
 *
 * @param {string} view
 * @param {Date}   anchor
 * @param {string} dateFrom ISO date string (used for week label).
 * @param {string} dateTo   ISO date string (used for week label).
 * @return {string}
 */
function formatHeading( view, anchor, dateFrom, dateTo ) {
	if ( view === 'month' ) {
		return `${
			MONTH_NAMES[ anchor.getMonth() ]
		} ${ anchor.getFullYear() }`;
	}
	if ( view === 'week' ) {
		return `${ dateFrom } – ${ dateTo }`;
	}
	if ( view === 'day' ) {
		return anchor.toLocaleDateString( undefined, {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		} );
	}
	// list
	return `${ MONTH_NAMES[ anchor.getMonth() ] } ${ anchor.getFullYear() }`;
}

const ALL_VIEWS = [ 'month', 'week', 'day', 'list' ];

/**
 * CalendarHeader
 *
 * @param {Object}   props
 * @param {string}   props.view
 * @param {Date}     props.anchor
 * @param {string}   props.dateFrom
 * @param {string}   props.dateTo
 * @param {string[]} props.availableViews  Which views to show in the switcher.
 * @param {boolean}  props.useLocalTz      Whether local timezone mode is active.
 * @param {Function} props.onToggleLocalTz Toggle local timezone mode.
 * @param {Function} props.onPrev
 * @param {Function} props.onNext
 * @param {Function} props.onToday
 * @param {Function} props.onViewChange
 */
export default function CalendarHeader( {
	view,
	anchor,
	dateFrom,
	dateTo,
	availableViews,
	useLocalTz,
	onToggleLocalTz,
	onPrev,
	onNext,
	onToday,
	onViewChange,
} ) {
	// Only show views that are in the availableViews list (preserve canonical order).
	const views =
		availableViews && availableViews.length
			? ALL_VIEWS.filter( ( v ) => availableViews.includes( v ) )
			: ALL_VIEWS;

	const siteTz = getTimezoneAbbr();
	const localTz = getLocalTimezoneAbbr();

	// Only show the toggle if the site timezone differs from the browser's.
	const showTzToggle = siteTz && localTz && siteTz !== localTz;

	return (
		<div className="nb-calendar-header">
			<div className="nb-calendar-nav">
				<button
					className="nb-calendar-nav-btn"
					onClick={ onPrev }
					aria-label="Previous"
				>
					&#8249;
				</button>
				<span className="nb-calendar-title">
					{ formatHeading( view, anchor, dateFrom, dateTo ) }
				</span>
				<button
					className="nb-calendar-nav-btn"
					onClick={ onNext }
					aria-label="Next"
				>
					&#8250;
				</button>
				<button className="nb-calendar-today-btn" onClick={ onToday }>
					{ strings.today || 'Today' }
				</button>

				{ showTzToggle && (
					<button
						className={ `nb-calendar-tz-toggle${
							useLocalTz ? ' nb-calendar-tz-toggle--active' : ''
						}` }
						onClick={ onToggleLocalTz }
						title={
							useLocalTz
								? strings.showEventTz ||
								  `Show in event timezone (${ siteTz })`
								: strings.showLocalTz ||
								  `Show in my timezone (${ localTz })`
						}
						aria-pressed={ useLocalTz }
					>
						{ useLocalTz ? localTz : siteTz }
					</button>
				) }
			</div>

			<div
				className="nb-calendar-view-switcher"
				role="group"
				aria-label="Calendar view"
			>
				{ views.map( ( v ) => (
					<button
						key={ v }
						className={ `nb-calendar-view-btn${
							v === view ? ' nb-calendar-view-btn--active' : ''
						}` }
						onClick={ () => onViewChange( v ) }
						aria-pressed={ v === view }
					>
						{ strings[ v ] ||
							v.charAt( 0 ).toUpperCase() + v.slice( 1 ) }
					</button>
				) ) }
			</div>
		</div>
	);
}
